import { Modal } from '../modules/modal.js';

app.pages = app.pages || {};

/**
 * Legal consent page handler - GDPR compliance, cookie preferences, and TOS acceptance
 */
app.pages.legal = {
    // Constants
    FIELDS: {
        COOKIES_FUNCTIONAL: 'paCookiesFunctional',
        COOKIES_OTHER: 'paCookiesOther',
        LEGAL_RESIDENCE: 'paLegalResidence',
        LEGAL_INVESTORS: 'paLegalInvestors',
        LEGAL_TOS: 'paLegalTos',
        LEGAL_REMEMBER: 'paLegalRemember'
    },

    SELECTORS: {
        LEGAL_MODAL: '#legalModal',
        LEGAL_FORM: 'form[name="legal"]',
        FORM_CONFIRM: '.form-confirm',
        REQUIRED_FIELDS: '.required',
        INPUTS: 'input, select'
    },

    ACTIONS: {
        ACCEPT_ALL: 'acceptall',
        REJECT_ALL: 'rejectall'
    },

    COOKIE_EXPIRATION: 365 * 24 * 60 * 60 * 1000, // 365 days in milliseconds
    REDIRECT_TIME: 7, // in seconds

    // Not allowed values per field
    NOT_ALLOWED: {
        paLegalResidence: ['other'],
        paLegalInvestors: ['private'],
    },

    // State
    dialog: null,
    form: null,
    confirm: null,
    modalInstance: null,
    redirectTimeout: null,
    redirectInterval: null,
    redirectIntervalValue: null,

    /**
     * Initialize the legal consent system
     */
    init: () => {
        const { legal } = app.pages;
        
        // Get DOM elements
        legal.dialog = document.getElementById('legalModal');
        legal.form = document.querySelector(legal.SELECTORS.LEGAL_FORM);
        
        if (!legal.form) {
            // This is normal - if user already consented, server won't provide the form
            return;
        }

        legal.confirm = legal.form.querySelector(legal.SELECTORS.FORM_CONFIRM);

        try {
            legal.initializeModal();
            legal.initializeForm();
            app.notifications.show('info', 'Legal consent system initialized successfully');
        } catch (error) {
            app.notifications.show('error', 'Failed to initialize legal consent system:', error);
        }
    },

    /**
     * Initialize the modal if needed
     */
    initializeModal: () => {
        const { legal } = app.pages;
        
        if (!legal.dialog) return;

        const alreadyAccepted = app.helpers.cookie.get(legal.FIELDS.LEGAL_TOS) === 'yes';
        
        if (!alreadyAccepted) {

            let pathname = window.location.pathname;
            pathname = pathname.replace(/\/paLegalResidence:[^\/]+/g, '');
            window.history.replaceState({}, '', pathname + window.location.search + window.location.hash);

            legal.modalInstance = new Modal({
                modal: legal.dialog,
                allowDeeplinking: false,
                allowHistory: false,
                startsOpen: true,
                allowClosing: false,
                hasCloseButton: false,
                on: {
                    ready: (modal, instance) => {
                        app.site.controls.fullstop();
                    }
                }
            });
        }
    },

    /**
     * Initialize form event handlers
     */
    initializeForm: () => {
        const { legal } = app.pages;
        
        legal.form.addEventListener('submit', legal.handleSubmit);
        legal.setupInputChangeHandlers();


        const reset = legal.form.querySelector('#paLegalButtonCancel');
        if (reset) {
            reset.addEventListener('click', (event) => {
                event.preventDefault();
                legal.resetForm();
            });
        }

    },

    /**
     * Reset form
     */
    resetForm: () => {
        const { legal } = app.pages;
        legal.form.reset();
        legal.clearRedirect();
        legal.form.querySelectorAll('div.form.select').forEach(s => {
            s.classList.add('is-placeholder');
        });
    },

    /**
     * Validation framework for different field types
     */
    validators: {
        /**
         * Validate required fields
         * @param {HTMLElement} field - Field to validate
         * @returns {boolean} True if valid
         */
        required: (field) => {
            let value;
            
            if (field.matches('.select')) {
                value = field.querySelector('select').value;
            } else if (field.matches('.checkbox')) {
                value = field.querySelector('input[type="checkbox"]:checked')?.value ?? false;
            } else if (field.matches('.toggle')) {
                value = field.querySelector('input[type="checkbox"]:checked')?.value ?? false;
            }
            
            return value && value !== 'default-preselect';
        },

        /**
         * Validate residence field (special case for not allowed countries)
         * @param {HTMLElement} residenceElement - Residence form element
         * @returns {boolean} True if valid
         */
        residence: (residenceElement) => {
            if (!residenceElement) {
                return true;
            }
            
            const { legal } = app.pages;
            const fieldName = residenceElement.name; // e.g., 'paLegalResidence'
            const notAllowedValues = legal.NOT_ALLOWED[fieldName] || [];
            
            // Check if residence value is in the field-specific not allowed list
            return !notAllowedValues.includes(residenceElement.value);
        },

        /**
         * Validate investor field (special case for not allowed countries)
         * @param {HTMLElement} investorElement - Investor form element
         * @returns {boolean} True if valid
         */
        investor: (investorElement) => {
            if (!investorElement) {
                return true;
            }
            
            const { legal } = app.pages;
            const fieldName = investorElement.name; // e.g., 'paLegalResidence'
            const notAllowedValues = legal.NOT_ALLOWED[fieldName] || [];
            
            // Check if residence value is in the field-specific not allowed list
            return !notAllowedValues.includes(investorElement.value);
        }
    },

    /**
     * Get all possible form elements
     * @returns {Object} Object containing form elements that exist
     */
    getFormElements: () => {
        const { legal } = app.pages;
        
        const possibleElements = {
            [legal.FIELDS.COOKIES_FUNCTIONAL]: legal.form.querySelector(`[name="${legal.FIELDS.COOKIES_FUNCTIONAL}"]`),
            [legal.FIELDS.COOKIES_OTHER]: legal.form.querySelector(`[name="${legal.FIELDS.COOKIES_OTHER}"]`),
            [legal.FIELDS.LEGAL_RESIDENCE]: legal.form.querySelector(`[name="${legal.FIELDS.LEGAL_RESIDENCE}"]`),
            [legal.FIELDS.LEGAL_INVESTORS]: legal.form.querySelector(`[name="${legal.FIELDS.LEGAL_INVESTORS}"]`),
            [legal.FIELDS.LEGAL_TOS]: legal.form.querySelector(`[name="${legal.FIELDS.LEGAL_TOS}"]`),
            [legal.FIELDS.LEGAL_REMEMBER]: legal.form.querySelector(`[name="${legal.FIELDS.LEGAL_REMEMBER}"]`)
        };

        // Filter out null elements
        return Object.fromEntries(
            Object.entries(possibleElements).filter(([key, value]) => value !== null)
        );
    },

    /**
     * Validate the entire form
     * @param {Object} formElements - Form elements to validate
     * @returns {Array} Array of fields with validation errors
     */
    validateForm: (formElements) => {
        const { legal } = app.pages;
        const errorFields = [];
        const requiredFields = legal.form.querySelectorAll(legal.SELECTORS.REQUIRED_FIELDS);
        
        // Validate required fields
        requiredFields.forEach(field => {
            field.classList.remove('is-error');
            if (!legal.validators.required(field)) {
                errorFields.push(field);
                field.classList.add('is-error');
            }
        });

        // Special validation for residence "other" selection
        const residenceElement = formElements[legal.FIELDS.LEGAL_RESIDENCE];
        if (residenceElement && !legal.validators.residence(residenceElement)) {
            const residenceField = residenceElement.closest('div.form');
            errorFields.push(residenceField);
            residenceField.classList.add('is-error');
        }

        // Special validation for investor selection
        const investorElement = formElements[legal.FIELDS.LEGAL_INVESTORS];
        const errorEl = document.getElementById(`error-${investorElement.name}`);
        if (investorElement && !legal.validators.investor(investorElement)) {
            const investorField = investorElement.closest('div.form');
            errorFields.push(investorField);
            investorField.classList.add('is-error');
            if (errorEl) {
                errorEl.classList.add('is-error');
            }
        } else {
            if (errorEl) {
                errorEl.classList.remove('is-error');
            }
        }

        return errorFields;
    },

    /**
     * Handle validation errors
     * @param {Array} errorFields - Array of fields with errors
     */
    handleValidationErrors: (errorFields) => {
        if (errorFields.length > 0) {
            errorFields[0].scrollIntoView();
            app.notifications.show('warn', 'Form validation failed - please check required fields', errorFields);
        }
    },

    /**
     * Process form values and apply action-specific logic
     * @param {Object} formElements - Form elements
     * @param {Event} event - Submit event
     * @returns {Object} Processed values
     */
    processFormValues: (formElements, event) => {
        const { legal } = app.pages;
        
        const values = Object.keys(formElements).reduce((acc, key) => {
            const element = formElements[key];
            if (element.type === "checkbox") {
                acc[key] = element.checked ? element.value : null;
            } else {
                acc[key] = element.value;
            }
            return acc;
        }, {});

        // Apply action-specific overrides
        const action = event.submitter?.getAttribute('href')?.substring(1);
        legal.applyActionOverrides(values, action);

        return values;
    },

    /**
     * Apply action-specific value overrides
     * @param {Object} values - Current form values
     * @param {string} action - Action type (acceptall, rejectall, etc.)
     */
    applyActionOverrides: (values, action) => {
        const { legal } = app.pages;
        
        switch (action) {
            case legal.ACTIONS.ACCEPT_ALL:
                values[legal.FIELDS.COOKIES_OTHER] = 'yes';
                break;
            case legal.ACTIONS.REJECT_ALL:
                values[legal.FIELDS.COOKIES_OTHER] = null;
                break;
            default:
                // No overrides for custom selections
                break;
        }
    },

    /**
     * Save user preferences to cookies
     * @param {Object} values - Values to save
     */
    savePreferences: async (values) => {
        const { legal } = app.pages;
        
        try {
            Object.keys(values).forEach(key => {
                if (values[key] === null) {
                    app.helpers.cookie.delete(key);
                } else {
                    app.helpers.cookie.set(key, values[key], legal.COOKIE_EXPIRATION);
                }
            });
            
            app.notifications.show('info', 'Cookie preferences saved');
            
        } catch (error) {
            app.notifications.show('error', 'Failed to save cookie preferences:', error);
            throw error;
        }
    },

    /**
     * Handle successful form submission
     */
    handleSuccessfulSubmission: () => {
        const { legal } = app.pages;
        
        try {
            if (legal.dialog && legal.modalInstance) {
                Modal.close(legal.dialog);
            } else {
                legal.showConfirmationMessage();
            }

            // Rebuild tracking with new consent preferences
            if (app.site.tracking?.build) {
                app.site.tracking.build();
                app.notifications.show('info', 'Tracking system updated with new preferences');
            }
            
        } catch (error) {
            app.notifications.show('error', 'Error during successful submission handling:', error);
        }
    },

    /**
     * Show confirmation message for non-modal submissions
     */
    showConfirmationMessage: () => {
        const { legal } = app.pages;
        
        if (legal.confirm) {
            legal.confirm.classList.add('visible');
            legal.setupInputChangeHandlers();
        }
    },

    /**
     * Handle input changes to hide confirmation and reset validation
     */
    handleInputChange: () => {
        const { legal } = app.pages;

        try {
            // Clear any active redirect first
            legal.clearRedirect();

            // Check for Swiss institutional investor combination
            legal.checkSelectionCombination();

            // Hide confirmation message
            if (legal.confirm) {
                legal.confirm.classList.remove('visible');
                legal.removeInputHandlers();
            }
        } catch (error) {
            app.notifications.show('error', 'Error handling input change:', error);
        }
    },

    /**
     * Check if user is Swiss institutional investor and handle accordingly
     */
    checkSelectionCombination: () => {
        const { legal } = app.pages;

        // if (document.documentElement.getAttribute('region') !== 'CH') {
        //     return;
        // }

        const residenceElement = legal.form.querySelector(`[name="${legal.FIELDS.LEGAL_RESIDENCE}"]`);
        const investorsElement = legal.form.querySelector(`[name="${legal.FIELDS.LEGAL_INVESTORS}"]`);
        
        if (!residenceElement || !investorsElement) {
            return;
        }

        const residence = residenceElement.value;
        const investors = investorsElement.value;

        if (residence === 'default-preselect') return;
        
        legal.handleRedirect(residence);
    },

    /**
     * Clear active redirect and hide redirect message
     */
    clearRedirect: () => {
        const { legal } = app.pages;
        
        // Clear the timeout if it exists
        if (legal.redirectTimeout) {
            clearTimeout(legal.redirectTimeout);
            legal.redirectTimeout = null;
        }

        if (legal.redirectInterval) {
            clearInterval(legal.redirectInterval);
            legal.redirectInterval = null;
            legal.redirectIntervalValue = null;
        }
        
        // Hide the redirect message
        const redirectMessage = document.querySelector('.legal-redirect-message');
        const redirectForm = document.querySelector('.legal-redirect-hidden');
        if (redirectMessage) {
            redirectMessage.style.display = 'none';
        }
        if (redirectForm) {
            redirectForm.style.display = 'block';
        }

        // hide all redirect specific elements
        const redirectElements = document.querySelectorAll('.legal-redirect-regio');
        redirectElements.forEach(r => r.classList.add('hidden'));

    },

    /**
     * Handle Redirects
     */
    handleRedirect: (regio) => {
        const { legal } = app.pages;

        const currentPageRegio = (document.documentElement.getAttribute('region') || 'CH').toLowerCase();
        regio = regio.toLowerCase();

        if (currentPageRegio === regio || regio === 'other') return;

        try {


            // Show the redirect notice element
            const redirectElements = document.querySelectorAll(`.legal-redirect-regio-${regio}`);
            redirectElements.forEach(r => r.classList.remove('hidden'));


            const redirectMessage = document.querySelector('.legal-redirect-message');
            if (redirectMessage) {
                redirectMessage.style.display = 'block';
            }

            const redirectForm = document.querySelector('.legal-redirect-hidden');
            if (redirectForm) {
                redirectForm.style.display = 'none';
            }
            

            // Construct redirect URL
            const redirectButton = document.querySelector(`a.button.legal-redirect-regio-${regio}`);
            const redirectUrl = redirectButton ? redirectButton.getAttribute('href') : false;

            if (redirectUrl) {
                legal.redirectTimeout = setTimeout(() => {
                    window.location = redirectUrl;
                    if (legal.redirectInterval) {
                        clearInterval(legal.redirectInterval);
                        legal.redirectInterval = null;
                    }
                }, legal.REDIRECT_TIME * 1000);
            }

            const paLegalRedirectSeconds = document.querySelectorAll('span.paLegalRedirectSeconds');
            legal.redirectIntervalValue = legal.REDIRECT_TIME;

            paLegalRedirectSeconds.forEach(s => s.innerHTML = legal.redirectIntervalValue);

            legal.redirectInterval = setInterval(() => {
                legal.redirectIntervalValue = legal.redirectIntervalValue - 1;
                paLegalRedirectSeconds.forEach(s => s.innerHTML = legal.redirectIntervalValue);
                if (legal.redirectIntervalValue <= 0) {
                    clearInterval(legal.redirectInterval);
                    legal.redirectInterval = null;
                }
            }, 1000);
            
            app.notifications.show('info', `Redirecting to ${regio} site in 5 seconds: ${redirectUrl}`);
            
        } catch (error) {
            app.notifications.show('error', `Error handling ${regio} institutional redirect:`, error);
        }
    },

    /**
     * Setup input change handlers for form validation feedback
     */
    setupInputChangeHandlers: () => {
        const { legal } = app.pages;
        const inputs = legal.form.querySelectorAll(legal.SELECTORS.INPUTS);
        
        inputs.forEach(input => {
            input.addEventListener('change', legal.handleInputChange, true);
        });
    },

    /**
     * Remove input change handlers
     */
    removeInputHandlers: () => {
        const { legal } = app.pages;
        const inputs = legal.form.querySelectorAll(legal.SELECTORS.INPUTS);
        
        inputs.forEach(input => {
            input.removeEventListener('change', legal.handleInputChange, true);
        });
    },

    /**
     * Handle form submission
     * @param {Event} event - Form submit event
     */
    handleSubmit: async (event) => {
        event.preventDefault();
        
        const { legal } = app.pages;
        
        try {
            const formElements = legal.getFormElements();
            const errorFields = legal.validateForm(formElements);
            
            if (errorFields.length > 0) {
                legal.handleValidationErrors(errorFields);
                return;
            }

            const values = legal.processFormValues(formElements, event);
            await legal.savePreferences(values);
            legal.handleSuccessfulSubmission();
            
            app.notifications.show('info', 'Legal preferences saved successfully');
            
        } catch (error) {
            app.notifications.show('error', 'Failed to process legal form submission:', error);
        }
    },

    /**
     * Get current consent status
     * @returns {Object} Current consent status
     */
    getCurrentConsent: () => {
        const { legal } = app.pages;
        
        return {
            functionalCookies: app.helpers.cookie.get(legal.FIELDS.COOKIES_FUNCTIONAL),
            otherCookies: app.helpers.cookie.get(legal.FIELDS.COOKIES_OTHER),
            tosAccepted: app.helpers.cookie.get(legal.FIELDS.LEGAL_TOS),
            residence: app.helpers.cookie.get(legal.FIELDS.LEGAL_RESIDENCE),
            investorStatus: app.helpers.cookie.get(legal.FIELDS.LEGAL_INVESTORS),
            rememberPreferences: app.helpers.cookie.get(legal.FIELDS.LEGAL_REMEMBER)
        };
    },

    /**
     * Check if user has completed legal consent process
     * @returns {boolean} True if consent is complete
     */
    hasValidConsent: () => {
        const { legal } = app.pages;
        const consent = legal.getCurrentConsent();
        return consent.tosAccepted === 'yes';
    }
};